import { computed, inject, Signal } from '@angular/core';
import { accountModuleStore } from '@app/reducers/account-module/account-module.state';
import { selectAccountSubscriptionReady, selectPlanType } from '@app/reducers/account/account-subscription.selector';
import {
  selectAccountCountryCode,
  selectAccountHasHrPlus,
  selectAccountId,
  selectAccountIsBetaAccount,
  selectAccountResellerId,
} from '@app/reducers/account/account.selector';
import { selectAuthUserId, selectIsAccountManager } from '@app/reducers/auth/auth.selectors';
import { AppState } from '@app/reducers/interfaces';
import { userPreferencesStore } from '@app/signal-store/user-preferences/user-preferences.store';
import { createSelector, Store } from '@ngrx/store';
import { FeatureServiceContext } from '@sb/shared/feature';
import { environment } from 'src/environments/environment';

const selectFeatureServiceContext = createSelector(
  selectAccountId,
  selectAccountCountryCode,
  selectAccountIsBetaAccount,
  selectAccountHasHrPlus,
  selectAccountResellerId,
  selectAuthUserId,
  selectIsAccountManager,
  selectPlanType,
  selectAccountSubscriptionReady,
  accountModuleStore.selectChatOptInEnabled,
  (
    accountId,
    accountCountryCode,
    isBetaAccount,
    isHrProOptIn,
    resellerId,
    userId,
    isAccountManager,
    planType,
    accountSubscriptionReady,
    isChatOptedIn,
  ): Omit<FeatureServiceContext, 'isLabsUser' | 'appVersion' | 'instance'> | undefined => {
    if (!userId || !accountId || !accountSubscriptionReady) {
      return undefined;
    }
    return {
      userId,
      accountId,
      isAccountManager,
      plan: planType,
      resellerId,
      isBetaAccount,
      isHrProOptIn,
      isChatOptedIn,
      accountCountry: accountCountryCode ?? '',
    };
  },
);

/** Returns the feature service context signal for use with provideFeatureService({ context }). */
export const useFeatureServiceContext = (): Signal<FeatureServiceContext | undefined> => {
  const userPreferences = inject(userPreferencesStore);
  const context = inject(Store<AppState>).selectSignal(selectFeatureServiceContext);
  return computed(() => {
    const ctx = context();
    if (ctx) {
      return {
        ...ctx,
        appVersion: environment.version,
        instance: environment.instance,
        isLabsUser: userPreferences.labs_enabled() ?? false,
      };
    }
    return undefined;
  });
};
